import Typography from '@mui/material/Typography';
import { AxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { RutterConnection } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { DisconnectIntegrationErrorComponent } from '~pages/Settings/Integrations/DisconnectIntegrationErrorComponent';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';

interface RutterDisconnectDialogProps {
  connection: Nullable<RutterConnection>;
  isOpen: boolean;
  onClose: () => void;
  onDisconnected: () => void;
}

const RutterDisconnectDialog = observer(
  ({ connection, isOpen, onClose, onDisconnected }: RutterDisconnectDialogProps) => {
    const { rutterIntegrationStore, toasterStore } = useStores();
    const { deleteConnection } = useRutter();
    const [shouldShowError, setShouldShowError] = useState(false);
    const isDeleting = connection
      ? rutterIntegrationStore.isDeletingConnection.get(connection.id)
      : false;

    const handleClose = () => {
      if (isDeleting) {
        return;
      }

      setShouldShowError(false);
      onClose();
    };

    const handleConfirm = async () => {
      if (!connection) {
        return;
      }

      try {
        rutterIntegrationStore.deleteConnectionStart(connection.id);
        await deleteConnection(connection.id, (error: AxiosError) => {
          setShouldShowError(Boolean(error.response?.status || error.message));
        });
        rutterIntegrationStore.deleteConnectionEnd(connection.id, true);
        setShouldShowError(false);
        toasterStore.push(
          alert(
            t('administration.integration.rutter.grid.connection_deleted'),
            AlertTypes.success,
          ),
        );
        onDisconnected();
      } catch {
        rutterIntegrationStore.deleteConnectionEnd(connection.id);
      }
    };

    return (
      <ConfirmationDialog
        confirmButtonProps={{ children: t('actions.disconnect') }}
        isBusy={isDeleting}
        isOpen={isOpen}
        onClose={handleClose}
        onConfirm={handleConfirm}
        title={t('administration.integration.rutter.cta.disconnect_integration', {
          integrationName:
            connection?.platform ?? t('administration.integration.rutter.name'),
        })}
      >
        <Typography>
          {t('administration.integration.rutter.cta.some_features_may_no_longer_work')}
        </Typography>
        {shouldShowError ? <DisconnectIntegrationErrorComponent /> : null}
      </ConfirmationDialog>
    );
  },
);

export default RutterDisconnectDialog;
